import { FC, memo, ReactNode, useState } from 'react'
import { ExtraProps } from 'react-markdown'
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css'
import js from 'react-syntax-highlighter/dist/esm/languages/prism/javascript'
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml'
import { CheckIcon, CopyIcon } from '~/constants/icon'
import { useThrottledState } from '~/hooks'
import { trace } from '~/services/analyticsService'
import { cm } from '~/utils/utils'
import styles from './code.module.scss'
import { HighlightedCode } from './HighlightedCode'

SyntaxHighlighter.registerLanguage('markdown', yaml)
SyntaxHighlighter.registerLanguage('yaml', yaml)
SyntaxHighlighter.registerLanguage('raml', yaml)
SyntaxHighlighter.registerLanguage('yml', yaml)
SyntaxHighlighter.registerLanguage('javascript', js)
SyntaxHighlighter.registerLanguage('dataweave', js)
SyntaxHighlighter.registerLanguage('dwl', js)
SyntaxHighlighter.registerLanguage('css', css)

interface Props {
  children: string
  className?: string
}

type CodeProps = Props & ExtraProps

export const Code: FC<CodeProps> = memo(({ children, className = '' }) => {
  return (
    <code
      className={cm(
        'rounded-[5px] border border-border adark:border-gray-700 bg-light1 dark:bg-medium6 py-[1px] px-[5px]'
      )}
    >
      {children}
    </code>
  )
})

interface PreProps {
  children: ReactNode
}

export const Pre: FC<PreProps> = ({ children }) => {
  const [isCopied, setIsCopied] = useState(false)

  const child = Array.isArray(children) ? children[0] : children
  const className = child?.props?.className || ''
  const languageMatch = /language-(\w+)/.exec(className)
  const code = useThrottledState(child.props.children, 250)
  const language = languageMatch ? languageMatch[1] : 'task'

  const copyToClipboard = () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) return
    const text = child.props.children.trim()
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    })
    trace({
      event: 'copy_to_clipboard',
      labels: { text, type: 'click' }
    })
  }

  return (
    <div
      className={cm(
        'codeblock relative font-sans text-[13px] my-2 px-2 border border-light-outline',
        'pr-10 pb-4',
        styles.codeblock,
        className
      )}
    >
      <button
        className={cm(
          'flex gap-1.5 items-center rounded-md bg-none text-xs absolute right-5 top-4',
          'bg-white border border-[#e9eaeb] p-[10px]',
          styles.copyButton
        )}
        onClick={copyToClipboard}
      >
        {isCopied ? (
          <CheckIcon className='h-[20px] w-[20px]' />
        ) : (
          <CopyIcon className='h-[20px] w-[20px]' />
        )}
      </button>
      <HighlightedCode code={code} language={language} />
    </div>
  )
}
